import { Link, Navigate } from "react-router-dom";
import { Footer } from "../components/Footer";
import { Header } from "../components/Header";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { KeepExploring } from "../components/RelatedLinks";
import { useLang, useLp } from "../lib/i18n";

const seoTitle = "Sem bot na reunião: IA para call em inglês sem participante extra";
const seoDescription =
  "Página prática sobre transcrição reunião sem bot: use IA ao vivo em calls em inglês sem colocar um participante extra no Zoom, Meet ou Teams.";

const riskMoments = [
  {
    title: "1:1 em inglês",
    body: "Feedback, promoção, prioridade e conflito com outro time ficam mais delicados quando uma presença extra aparece na lista de participantes.",
  },
  {
    title: "Call com cliente",
    body: "Discovery, QBR e renovação dependem de confiança. Um bot pode transformar uma conversa consultiva em uma gravação explícita antes da relação esquentar.",
  },
  {
    title: "Entrevista técnica",
    body: "Quando a pergunta chega rápido, você precisa de clareza e resposta curta em inglês, não de mais fricção social no começo da chamada.",
  },
];

const requirements = [
  "não entra como participante na chamada;",
  "funciona em Zoom, Google Meet e Microsoft Teams sem extensão específica;",
  "ajuda durante a call, não só depois;",
  "explica o fluxo de dados e processamento com clareza;",
  "sugere respostas curtas em inglês quando a pergunta é para você;",
  "mantém próximos passos e resumo para follow-up depois da reunião.",
];

const comparison = [
  {
    label: "Notetaker com bot",
    value: "Bom para ata depois da call, mas aparece para todo mundo e pode mudar o tom da conversa.",
  },
  {
    label: "Transcrição simples",
    value: "Registra o que foi dito, mas não ajuda quando você precisa responder ao vivo em inglês.",
  },
  {
    label: "Minuta",
    value: "Roda no canto da tela, sem bot na reunião, e sugere a próxima resposta enquanto a conversa ainda está acontecendo.",
  },
];

const faqs = [
  {
    q: "Sem bot quer dizer que ninguém vê o Minuta na call?",
    a: "Sim. O Minuta não entra como participante no Zoom, Meet ou Teams. Ele roda como app de desktop para apoiar você durante a conversa.",
  },
  {
    q: "Isso substitui consentimento ou política da empresa?",
    a: "Não. Você continua responsável por seguir regras internas, contrato com cliente e legislação aplicável. Sem bot reduz fricção social, mas não remove obrigações de uso correto.",
  },
  {
    q: "O Minuta funciona só para transcrição?",
    a: "Não. A proposta da Rota A é copiloto ao vivo para reunião difícil em inglês: transcrição, contexto e sugestões curtas enquanto a call acontece.",
  },
  {
    q: "Quanto custa?",
    a: "As primeiras 100 vagas Founding 100 ficam por R$22.50/mês por 12 meses. Depois, o Pro volta para R$35/mês.",
  },
];

export function SemBotNaReuniao() {
  const lang = useLang();
  const lp = useLp();

  if (lang !== "pt") {
    return <Navigate to="/pt/sem-bot-na-reuniao" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Seo
        title={seoTitle}
        description={seoDescription}
        path="/sem-bot-na-reuniao"
        alternateLangs={["pt"]}
        schema={["article", "faqPage", "breadcrumbList"]}
        schemaOptions={{
          article: {
            headline: seoTitle,
            datePublished: "2026-06-09",
            dateModified: "2026-06-09",
            author: "Minuta",
          },
          breadcrumbs: [
            { name: "Minuta", url: "/pt/" },
            { name: "Sem bot na reunião", url: "/pt/sem-bot-na-reuniao" },
          ],
        }}
      />
      <Header />

      <main className="flex-1">
        <section className="border-b border-margin dark:border-paper/10">
          <div className="mx-auto max-w-3xl px-6 pt-20 pb-16 sm:pt-28 sm:pb-20">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55">
              Guia prático · transcrição reunião sem bot
            </p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-ink dark:text-paper">
              Sem bot na reunião, com IA ajudando você durante a call.
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-ink/75 dark:text-paper/75">
              Minuta apoia calls difíceis em inglês sem aparecer na lista de
              participantes. Ele acompanha o que foi dito, ajuda a estruturar a
              próxima resposta e deixa a conversa com cara de conversa.
            </p>
            <p className="mt-5 font-mono text-[12px] uppercase tracking-[0.14em] text-ink/55 dark:text-paper/55">
              Zoom · Meet · Teams · grátis por 7 dias · Founding 100 por R$22.50/mês
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link
                to={lp + "/downloads?utm_source=seo&utm_medium=landing&utm_campaign=sem_bot_na_reuniao"}
                className="inline-flex items-center rounded-full bg-ink px-6 py-3 text-sm font-medium text-paper hover:bg-ink/90 transition-colors dark:bg-paper dark:text-ink dark:hover:bg-paper/90"
              >
                Baixar o Minuta
              </Link>
              <Link
                to={lp + "/blog/no-bot-in-the-call"}
                className="inline-flex items-center rounded-full border border-ink/15 dark:border-paper/15 px-6 py-3 text-sm font-medium text-ink dark:text-paper hover:bg-ink/5 dark:hover:bg-paper/5 transition-colors"
              >
                Ler o guia completo
              </Link>
            </div>
          </div>
        </section>

        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                O diferencial não é esconder IA. É não colocar outra pessoa na sala.
              </h2>
              <p className="mt-6 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                Um bot visível muda a reunião antes mesmo da primeira pergunta. Em
                calls sensíveis, o cliente, gestor ou entrevistador percebe que há
                uma presença extra registrando a conversa. Algumas reuniões aceitam
                isso bem. Outras ficam mais cuidadosas, menos diretas e menos úteis.
              </p>
              <p className="mt-4 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                A proposta de uma IA sem bot na reunião é diferente: ajudar você a
                entender, responder e fazer follow-up sem transformar o encontro em
                uma gravação com participante novo. O produto deve reduzir fricção,
                não criar uma negociação social no primeiro minuto.
              </p>
            </div>
          </section>
        </Reveal>

        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-5xl px-6 py-20">
              <div className="max-w-3xl">
                <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                  Onde sem bot pesa mais
                </h2>
                <p className="mt-6 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                  O padrão sem bot importa quando confiança, nuance e velocidade de
                  resposta valem mais do que uma ata perfeita depois.
                </p>
              </div>
              <div className="mt-10 grid gap-6 md:grid-cols-3">
                {riskMoments.map((item) => (
                  <article
                    key={item.title}
                    className="rounded-2xl border border-ink/10 bg-ink/[0.02] p-6 dark:border-paper/12 dark:bg-paper/[0.02]"
                  >
                    <h3 className="font-display text-xl font-semibold text-ink dark:text-paper">
                      {item.title}
                    </h3>
                    <p className="mt-3 text-[14px] leading-relaxed text-ink/75 dark:text-paper/75">
                      {item.body}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                O que exigir de uma solução sem bot
              </h2>
              <p className="mt-6 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                Tirar o bot só resolve metade do problema. Para uma call em inglês,
                a ferramenta também precisa ajudar no momento em que a pergunta
                chega, enquanto ainda dá tempo de responder melhor.
              </p>
              <ul className="mt-8 space-y-4">
                {requirements.map((item) => (
                  <li
                    key={item}
                    className="text-[15px] leading-relaxed text-ink/80 dark:text-paper/80"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </Reveal>

        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-4xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                Notetaker depois vs. copiloto durante
              </h2>
              <div className="mt-10 divide-y divide-ink/10 border-y border-ink/10 dark:divide-paper/12 dark:border-paper/12">
                {comparison.map((row) => (
                  <div key={row.label} className="grid gap-3 py-5 md:grid-cols-[180px_1fr]">
                    <p className="font-mono text-[12px] uppercase tracking-[0.14em] text-ink/55 dark:text-paper/55">
                      {row.label}
                    </p>
                    <p className="text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                      {row.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                Como testar na próxima call
              </h2>
              <p className="mt-6 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                Escolha uma reunião em que você normalmente hesitaria antes de colocar
                um bot: 1:1 com gestor global, call com cliente novo, entrevista ou
                negociação de prioridade. Abra o Minuta antes da call e use as
                sugestões como rascunho da próxima frase, não como script.
              </p>
              <p className="mt-4 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                Para aprofundar o contexto, veja o guia de{" "}
                <Link to={lp + "/ia-reuniao-ingles"} className="underline underline-offset-4">
                  IA para reunião em inglês
                </Link>{" "}
                e o artigo{" "}
                <Link to={lp + "/blog/no-bot-in-the-call"} className="underline underline-offset-4">
                  Sem bot na reunião: por que virou diferencial
                </Link>
                .
              </p>
            </div>
          </section>
        </Reveal>

        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                Perguntas comuns
              </h2>
              <div className="mt-8 space-y-7">
                {faqs.map((faq) => (
                  <div key={faq.q}>
                    <h3 className="font-display text-xl font-semibold text-ink dark:text-paper">
                      {faq.q}
                    </h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-ink/75 dark:text-paper/75">
                      {faq.a}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        <Reveal>
          <section>
            <div className="mx-auto max-w-3xl px-6 py-24 text-center">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                Entre na próxima reunião sem chamar um bot.
              </h2>
              <p className="mt-5 text-lg text-ink/70 dark:text-paper/70">
                7 dias grátis. Founding 100 por R$22.50/mês durante 12 meses para
                as primeiras 100 vagas.
              </p>
              <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to={lp + "/downloads?utm_source=seo&utm_medium=landing&utm_campaign=sem_bot_na_reuniao"}
                  className="inline-flex items-center rounded-full bg-ink px-6 py-3 text-sm font-medium text-paper hover:bg-ink/90 transition-colors dark:bg-paper dark:text-ink dark:hover:bg-paper/90"
                >
                  Baixar o Minuta
                </Link>
                <Link
                  to={lp + "/pricing"}
                  className="inline-flex items-center rounded-full border border-ink/15 dark:border-paper/15 px-6 py-3 text-sm font-medium text-ink dark:text-paper hover:bg-ink/5 dark:hover:bg-paper/5 transition-colors"
                >
                  Ver Founding 100
                </Link>
              </div>
            </div>
          </section>
        </Reveal>

        <KeepExploring />
      </main>

      <Footer />
    </div>
  );
}
